import React, { useState } from "react";
import axios from "axios";
import { 
  Sparkles, 
  Zap, 
  Send, 
  Flame, 
  Sliders, 
  Clock, 
  Check, 
  TrendingUp, 
  Radio, 
  FileText,
  DollarSign,
  Brain,
  Bot,
  HeartPulse
} from "lucide-react";
import { Button } from "./ui/button";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const NICHES = [
  { id: "dinero", label: "Dinero y Finanzas", icon: "💰" },
  { id: "productividad", label: "Productividad y Hábitos", icon: "⚡" },
  { id: "salud", label: "Salud y Longevidad", icon: "🧘" },
  { id: "psicologia", label: "Psicología Oscura", icon: "🧠" },
  { id: "ia", label: "Inteligencia Artificial", icon: "🤖" }
];

export const GeneratorForm = ({ onStarted, onCompleted, onError, onOpenPayPal, user }) => {
  const [niche, setNiche] = useState("psicologia");
  const [topic, setTopic] = useState("");
  const [tone, setTone] = useState("viral");
  const [fastMode, setFastMode] = useState(true);
  const canAutoPublish = user?.access_status?.auto_publish !== false; // plan Gratis: solo publicar a mano
  const [autoPublish, setAutoPublish] = useState(canAutoPublish);
  const [submitting, setSubmitting] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (user?.access_status && !user.access_status.can_generate) {
      onOpenPayPal();
      return;
    }

    setSubmitting(true);
    onStarted();

    try {
      const token = localStorage.getItem("cortoviral_token");
      const headers = token ? { Authorization: `Bearer ${token}` } : {};

      const res = await axios.post(
        `${API}/shorts/generate`,
        {
          niche,
          topic: topic.trim(),
          tone,
          fast_mode: fastMode,
          auto_publish: autoPublish && canAutoPublish
        },
        { headers, withCredentials: true }
      );

      onCompleted(res.data);
      setTopic("");
    } catch (err) {
      if (err.response?.status === 402) {
        onOpenPayPal();
      } else {
        const detail = err.response?.data?.detail || "Error generando el Short.";
        onError(typeof detail === "string" ? detail : JSON.stringify(detail));
      }
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="bg-[#12141D] border border-white/10 rounded-2xl p-6 shadow-2xl relative overflow-hidden space-y-5">
      <div className="flex items-center justify-between pb-3 border-b border-white/10">
        <div className="flex items-center gap-2.5">
          <div className="w-9 h-9 rounded-xl bg-red-600/20 text-red-500 flex items-center justify-center font-bold">
            <Zap className="w-5 h-5 fill-red-500" />
          </div>
          <div>
            <h3 className="text-base font-black text-white">Crear Short Automatizado</h3>
            <p className="text-xs text-slate-400">Guion con IA + escenas 1080×1920 + render FFmpeg</p>
          </div>
        </div>

        <div className="flex items-center gap-2">
          <span className="text-[11px] font-mono px-2.5 py-1 rounded-full bg-emerald-500/10 text-emerald-400 border border-emerald-500/20 flex items-center gap-1 font-semibold">
            Modo Rápido: ON
          </span>
        </div>
      </div>

      <form onSubmit={handleSubmit} className="space-y-4">
        {/* Selector de Nicho Temático idéntico al modelo de CortoViral */}
        <div>
          <label className="block text-xs font-bold uppercase tracking-wider text-slate-300 mb-2">
            Nicho Temático
          </label>
          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-5 gap-2">
            {NICHES.map((n) => {
              const isSelected = n.id === niche;
              return (
                <button
                  key={n.id}
                  type="button"
                  data-testid={`niche-btn-${n.id}`}
                  onClick={() => setNiche(n.id)}
                  className={`p-2.5 rounded-xl border text-left transition-all flex flex-col justify-between gap-1 text-xs ${
                    isSelected
                      ? "bg-red-600/20 border-red-500 text-white shadow-md shadow-red-900/30"
                      : "bg-[#1A1D2B] border-white/5 text-slate-400 hover:text-white hover:border-white/20"
                  }`}
                >
                  <span className="text-lg">{n.icon}</span>
                  <span className="font-semibold truncate">{n.label}</span>
                </button>
              );
            })}
          </div>
        </div>

        {/* Input Tema Opcional */}
        <div>
          <label className="block text-xs font-semibold uppercase tracking-wider text-slate-300 mb-1.5 flex items-center justify-between">
            <span>Tema Específico (Opcional - Dejar vacío para IA Auto)</span>
            <span className="text-slate-500 text-[10px] lowercase font-normal">español nativo</span>
          </label>
          <input
            data-testid="generator-topic-input"
            type="text"
            value={topic}
            onChange={(e) => setTopic(e.target.value)}
            placeholder="Ej: El efecto Zeigarnik o deja vacío para que la IA elija sola"
            className="w-full bg-[#1A1D2B] border border-white/10 rounded-xl px-4 py-3 text-sm text-white placeholder-slate-500 focus:outline-none focus:ring-2 focus:ring-red-500 transition-all"
          />
        </div>

        {/* Opciones y Publicación */}
        <div className="p-3 bg-black/40 border border-white/5 rounded-xl flex items-center justify-between">
          <label className="flex items-center gap-2.5 cursor-pointer select-none">
            <input
              data-testid="generator-autopublish-checkbox"
              type="checkbox"
              checked={autoPublish && canAutoPublish}
              disabled={!canAutoPublish}
              onChange={(e) => setAutoPublish(e.target.checked)}
              className="w-4 h-4 rounded text-red-600 bg-slate-900 border-white/20 focus:ring-red-500"
            />
            <span className="text-xs text-white font-semibold">
              Publicar automáticamente a YouTube al completar render
              {!canAutoPublish && (
                <button type="button" onClick={onOpenPayPal} className="block text-[10px] text-amber-400 font-normal underline">
                  🔒 Con la licencia (en el plan Gratis publicas desde «Mis Shorts»)
                </button>
              )}
            </span>
          </label>
          <span className="text-[11px] text-slate-400 font-mono hidden sm:inline">
            1080×1920 H.264
          </span>
        </div>

        <Button
          data-testid="generate-short-btn"
          type="submit"
          disabled={submitting}
          className="w-full bg-gradient-to-r from-red-600 via-rose-600 to-red-600 hover:from-red-500 hover:to-rose-500 text-white font-black py-4 rounded-xl shadow-lg shadow-red-900/30 text-sm tracking-wide flex items-center justify-center gap-2 uppercase transition-all"
        >
          <Sparkles className="w-4 h-4 fill-white" />
          {submitting ? "Fabricando Short... (puede tardar 1 min)" : "Probar Pipeline Ahora"}
        </Button>
      </form>
    </div>
  );
};
